/**
 * NotificationItem Component
 *
 * Individual notification card with type-specific styling and actions.
 * Supports budget alerts with detailed data display.
 *
 * @module NotificationItem
 * @created 2025-12-19 - TASK-030
 */

import { memo, useCallback } from 'react';
import type { Notification } from '@/types/notification';
import {
  getNotificationTypeConfig,
  isBudgetNotification,
  formatNotificationTime,
} from '@/types/notification';
import { formatCurrency } from '@/utils/format';

// ========================================
// Types
// ========================================

export interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
}

// ========================================
// Budget Alert Details Component
// ========================================

function BudgetAlertDetails({ notification }: { notification: Notification }) {
  const data = notification.data;
  if (!data) return null;

  const isExceeded = notification.type === 'budget_exceeded';

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className="text-gray-500">월 예산</span>
          <p className="font-medium text-gray-900">{formatCurrency(data.budgetAmount)}</p>
        </div>
        <div>
          <span className="text-gray-500">현재 지출</span>
          <p className={`font-medium ${isExceeded ? 'text-red-600' : 'text-amber-600'}`}>
            {formatCurrency(data.actualSpending)}
          </p>
        </div>
        <div>
          <span className="text-gray-500">사용률</span>
          <p className={`font-medium ${isExceeded ? 'text-red-600' : 'text-amber-600'}`}>
            {data.percentage.toFixed(1)}%
          </p>
        </div>
        {isExceeded && data.exceededAmount && (
          <div>
            <span className="text-gray-500">초과 금액</span>
            <p className="font-medium text-red-600">+{formatCurrency(data.exceededAmount)}</p>
          </div>
        )}
        {!isExceeded && (
          <div>
            <span className="text-gray-500">남은 예산</span>
            <p className="font-medium text-green-600">
              {formatCurrency(data.budgetAmount - data.actualSpending)}
            </p>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              isExceeded ? 'bg-red-500' : 'bg-amber-500'
            }`}
            style={{ width: `${Math.min(data.percentage, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// ========================================
// Main Component
// ========================================

export const NotificationItem = memo(function NotificationItem({
  notification,
  onMarkAsRead,
  onClick,
  compact = false,
}: NotificationItemProps) {
  const config = getNotificationTypeConfig(notification.type);
  const isBudget = isBudgetNotification(notification.type);

  const handleClick = useCallback(() => {
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
    if (onClick) {
      onClick(notification);
    }
  }, [notification, onMarkAsRead, onClick]);

  const handleMarkAsRead = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onMarkAsRead) {
        onMarkAsRead(notification.id);
      }
    },
    [notification.id, onMarkAsRead]
  );

  return (
    <div
      className={`
        relative p-4 border rounded-lg transition-all cursor-pointer
        ${
          notification.read
            ? 'bg-white border-gray-200 hover:border-gray-300'
            : 'bg-blue-50 border-blue-200 hover:border-blue-300'
        }
      `}
      onClick={handleClick}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-blue-500" />
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center text-xl`}
        >
          {config.icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bgColor} ${config.color}`}>
              {config.label}
            </span>
            {notification.yearMonth && (
              <span className="text-xs text-gray-500">{notification.yearMonth}</span>
            )}
          </div>

          <h4 className={`font-medium ${notification.read ? 'text-gray-700' : 'text-gray-900'}`}>
            {notification.title}
          </h4>

          {!compact && (
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">{notification.message}</p>
          )}

          {/* Timestamp */}
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {formatNotificationTime(notification.createdAt)}
            </span>

            {!notification.read && (
              <button
                type="button"
                onClick={handleMarkAsRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                읽음 표시
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Budget Alert Details (expanded view) */}
      {!compact && isBudget && <BudgetAlertDetails notification={notification} />}
    </div>
  );
});

export default NotificationItem;
